import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box, Typography, Paper, Button, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Alert, CircularProgress,
    IconButton, Tooltip, useTheme, Switch, Select, MenuItem,
    FormControl, InputLabel, FormControlLabel,
    Stack, Card, CardContent, Grid, alpha, LinearProgress,
    Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
    CloudUpload as UploadIcon, Download as DownloadIcon,
    Inventory as InventoryIcon, Category as CategoryIcon,
    CheckCircle as EnabledIcon, Cancel as DisabledIcon,
    CloudDownload as CloudDownloadIcon, Tune as ManageIcon,
    Edit as EditIcon, Verified as ChecksumIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNotification } from '../../contexts/NotificationContext';
import ConfirmDialog from '../../components/enterprise/ConfirmDialog';
import { toServerDate } from '../../timezone';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/';

/**
 * Fallback categories used before the backend list loads. The real catalog
 * comes from {@code GET /api/v2.0/downloads/categories/all} and replaces
 * this list on first fetch.
 */
const FALLBACK_CATEGORIES = [
    { slug: 'apm-agent', name: 'APM Agent', color: '8B5CF6' },
    { slug: 'collector', name: 'Collector', color: '0EA5E9' },
    { slug: 'sdk',       name: 'SDK',       color: 'F59E0B' },
    { slug: 'other',     name: 'Other',     color: '6B7280' },
];

const PLATFORMS = [
    { value: 'all', label: 'All Platforms' },
    { value: 'linux', label: 'Linux' },
    { value: 'windows', label: 'Windows' },
    { value: 'macos', label: 'macOS' },
];

const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
    }
    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (dt) => {
    if (!dt) return '-';
    return toServerDate(dt)?.toLocaleString() ?? dt;
};

const emptyFormState = {
    name: '',
    slug: '',
    category: 'apm-agent',
    description: '',
    version: '',
    requirements: '',
    platform: 'all',
    installInstructions: '',
    releaseNotes: '',
};

const emptyCategoryForm = {
    slug: '',
    name: '',
    description: '',
    icon: '',
    color: '',
    sortOrder: 100,
    enabled: true,
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const OperatorDownloadsPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { showNotification } = useNotification();

    const [artifacts, setArtifacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

    // Upload dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({ ...emptyFormState });
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    // Bytes uploaded so far (drives the "X.X MB / Y MB" label). Separate
    // from {@code uploadProgress} because for very small files
    // {@code progressEvent.total} can be 0 — we still want a sensible label.
    const [uploadedBytes, setUploadedBytes] = useState(0);
    // True once axios has flushed the body (progress = 100%) but the
    // server hasn't yet returned. Switches the bar to indeterminate so
    // the user knows we're not stuck — Spring is hashing + writing to disk.
    const [processing, setProcessing] = useState(false);

    // Categories admin
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ ...emptyCategoryForm });

    // Bulk selection
    const [selection, setSelection] = useState([]);

    // Delete confirmation
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    const fetchArtifacts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}api/operator/downloads`, {
                headers: getAuthHeaders(),
            });
            setArtifacts(res.data || []);
        } catch (err) {
            showNotification('Failed to load artifacts: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}api/v2.0/downloads/categories/all`, {
                headers: getAuthHeaders(),
            });
            if (Array.isArray(res.data) && res.data.length > 0) {
                setCategories(res.data);
            }
        } catch (err) {
            // Silent — fall back to FALLBACK_CATEGORIES. This endpoint is
            // new; on older backends it 404s and we keep the seeded list.
        }
    }, []);

    useEffect(() => {
        fetchArtifacts();
        fetchCategories();
    }, [fetchArtifacts, fetchCategories]);

    // Dynamic category lookup: the DataGrid chip + the upload form use this
    // to translate a slug to its display name / color.
    const categoryMap = useMemo(() => {
        const m = new Map();
        for (const c of categories) m.set(c.slug, c);
        return m;
    }, [categories]);

    const handleOpenUpload = () => {
        setFormData({ ...emptyFormState });
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadedBytes(0);
        setProcessing(false);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setFormData({ ...emptyFormState });
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadedBytes(0);
        setProcessing(false);
    };

    const handleFormChange = (field) => (e) => {
        const value = e.target.value;
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            // Auto-generate slug from name if slug hasn't been manually edited
            if (field === 'name' && (prev.slug === '' || prev.slug === slugify(prev.name))) {
                next.slug = slugify(value);
            }
            return next;
        });
    };

    const slugify = (str) =>
        str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!formData.name.trim() || !formData.slug.trim() || !selectedFile) return;

        const data = new FormData();
        data.append('file', selectedFile);
        data.append('name', formData.name.trim());
        data.append('slug', formData.slug.trim());
        data.append('category', formData.category);
        data.append('description', formData.description.trim());
        data.append('version', formData.version.trim());
        data.append('requirements', formData.requirements.trim());
        data.append('platform', formData.platform);
        if (formData.installInstructions && formData.installInstructions.trim()) {
            data.append('installInstructions', formData.installInstructions.trim());
        }
        if (formData.releaseNotes && formData.releaseNotes.trim()) {
            data.append('releaseNotes', formData.releaseNotes.trim());
        }

        try {
            setUploading(true);
            setUploadProgress(0);
            setUploadedBytes(0);
            setProcessing(false);
            await axios.post(`${API_BASE_URL}api/operator/downloads`, data, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const total = progressEvent.total || selectedFile.size || 1;
                    const pct = Math.round((progressEvent.loaded * 100) / total);
                    setUploadProgress(Math.min(pct, 100));
                    setUploadedBytes(progressEvent.loaded);
                    // When the body's flushed, axios won't fire any more
                    // progress events but the server is still hashing the
                    // file and writing the row. Flip to indeterminate so
                    // the user sees movement instead of a frozen 100%.
                    if (pct >= 100) setProcessing(true);
                },
            });
            showNotification(`Artifact "${formData.name}" uploaded successfully`, 'success');
            handleCloseDialog();
            fetchArtifacts();
        } catch (err) {
            showNotification('Upload failed: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
            setUploading(false);
            setProcessing(false);
        }
    };

    const handleToggleEnabled = async (artifact) => {
        try {
            await axios.put(
                `${API_BASE_URL}api/operator/downloads/${artifact.id}/toggle`,
                null,
                { headers: getAuthHeaders() }
            );
            showNotification(
                `"${artifact.name}" ${artifact.enabled ? 'disabled' : 'enabled'} successfully`,
                'success'
            );
            fetchArtifacts();
        } catch (err) {
            showNotification('Toggle failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_BASE_URL}api/operator/downloads/${deleteDialog.id}`, {
                headers: getAuthHeaders(),
            });
            setDeleteDialog({ open: false, id: null, name: '' });
            showNotification('Artifact deleted', 'success');
            fetchArtifacts();
        } catch (err) {
            showNotification('Delete failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    // ── Bulk selection actions ─────────────────────────────────────────
    const handleBulkDelete = async () => {
        const ids = [...selection];
        try {
            await Promise.all(ids.map((id) =>
                axios.delete(`${API_BASE_URL}api/operator/downloads/${id}`, { headers: getAuthHeaders() })
            ));
            showNotification(`Deleted ${ids.length} artifact(s)`, 'success');
            setSelection([]);
            setBulkDeleteOpen(false);
            fetchArtifacts();
        } catch (err) {
            showNotification('Bulk delete failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    const handleBulkToggle = async (enable) => {
        const ids = [...selection];
        try {
            await Promise.all(ids.map((id) => {
                const a = artifacts.find((x) => x.id === id);
                if (!a || a.enabled === enable) return Promise.resolve();
                return axios.put(`${API_BASE_URL}api/operator/downloads/${id}/toggle`, null, { headers: getAuthHeaders() });
            }));
            showNotification(`${enable ? 'Enabled' : 'Disabled'} ${ids.length} artifact(s)`, 'success');
            setSelection([]);
            fetchArtifacts();
        } catch (err) {
            showNotification('Bulk toggle failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    // ── Category CRUD ──────────────────────────────────────────────────
    const openCategoryCreate = () => {
        setEditingCategory(null);
        setCategoryForm({ ...emptyCategoryForm });
        setCategoryDialogOpen(true);
    };

    const openCategoryEdit = (cat) => {
        setEditingCategory(cat);
        setCategoryForm({
            slug: cat.slug || '',
            name: cat.name || '',
            description: cat.description || '',
            icon: cat.icon || '',
            color: cat.color || '',
            sortOrder: cat.sortOrder ?? 100,
            enabled: cat.enabled !== false,
        });
        setCategoryDialogOpen(true);
    };

    const saveCategory = async () => {
        try {
            if (editingCategory) {
                await axios.put(
                    `${API_BASE_URL}api/v2.0/downloads/categories/${editingCategory.id}`,
                    categoryForm,
                    { headers: getAuthHeaders() }
                );
                showNotification('Category updated', 'success');
            } else {
                await axios.post(
                    `${API_BASE_URL}api/v2.0/downloads/categories`,
                    categoryForm,
                    { headers: getAuthHeaders() }
                );
                showNotification('Category created', 'success');
            }
            setCategoryDialogOpen(false);
            fetchCategories();
        } catch (err) {
            showNotification('Save failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    const deleteCategory = async (cat) => {
        // Refuse if any artifact is using the slug — the user would have to
        // re-assign first. We check client-side; backend also allows the
        // delete but the chip would orphan.
        const inUse = artifacts.some((a) => a.category === cat.slug);
        if (inUse) {
            showNotification(`Reassign artifacts off "${cat.name}" first`, 'warning');
            return;
        }
        try {
            await axios.delete(`${API_BASE_URL}api/v2.0/downloads/categories/${cat.id}`, {
                headers: getAuthHeaders(),
            });
            showNotification('Category deleted', 'success');
            fetchCategories();
        } catch (err) {
            showNotification('Delete failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    // Stats
    const totalArtifacts = artifacts.length;
    const enabledArtifacts = artifacts.filter((a) => a.enabled).length;
    const totalDownloads = artifacts.reduce((sum, a) => sum + (a.downloads || 0), 0);
    const uniqueCategories = new Set(artifacts.map((a) => a.category).filter(Boolean)).size;

    const statsCards = [
        {
            label: 'Total Artifacts',
            value: totalArtifacts,
            icon: <InventoryIcon />,
            color: theme.palette.primary.main,
        },
        {
            label: 'Enabled',
            value: enabledArtifacts,
            icon: <EnabledIcon />,
            color: theme.palette.success.main,
        },
        {
            label: 'Total Downloads',
            value: totalDownloads.toLocaleString(),
            icon: <CloudDownloadIcon />,
            color: theme.palette.info.main,
        },
        {
            label: 'Categories',
            value: uniqueCategories,
            icon: <CategoryIcon />,
            color: theme.palette.warning.main,
        },
    ];

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {params.value}
                    </Typography>
                    {params.row.slug && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {params.row.slug}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'category',
            headerName: 'Category',
            width: 140,
            renderCell: (params) => {
                const slug = params.value || 'other';
                const cat = categoryMap.get(slug);
                const color = '#' + (cat?.color || '6B7280');
                const label = cat?.name || slug;
                return (
                    <Chip
                        label={label}
                        size="small"
                        sx={{
                            bgcolor: alpha(color, 0.12),
                            color: color,
                            fontWeight: 600,
                            border: `1px solid ${alpha(color, 0.3)}`,
                        }}
                    />
                );
            },
        },
        {
            field: 'checksum',
            headerName: 'Checksum',
            width: 120,
            renderCell: (params) => params.value ? (
                <Tooltip title={params.value}>
                    <Chip size="small" icon={<ChecksumIcon fontSize="small" />}
                          label={params.value.split(':')[1]?.substring(0, 8) || 'verified'}
                          color="success" variant="outlined" />
                </Tooltip>
            ) : (
                <Typography variant="caption" color="text.disabled">—</Typography>
            ),
        },
        {
            field: 'version',
            headerName: 'Version',
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value || '-'}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            ),
        },
        {
            field: 'platform',
            headerName: 'Platform',
            width: 110,
            renderCell: (params) => {
                const label = PLATFORMS.find((p) => p.value === params.value)?.label || params.value || 'All';
                return (
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {label}
                    </Typography>
                );
            },
        },
        {
            field: 'fileSize',
            headerName: 'Size',
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {formatFileSize(params.value)}
                </Typography>
            ),
        },
        {
            field: 'downloads',
            headerName: 'Downloads',
            width: 110,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {(params.value || 0).toLocaleString()}
                </Typography>
            ),
        },
        {
            field: 'enabled',
            headerName: 'Enabled',
            width: 100,
            renderCell: (params) => (
                <Switch
                    checked={!!params.value}
                    onChange={() => handleToggleEnabled(params.row)}
                    size="small"
                    color="success"
                />
            ),
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 140,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(params.value)}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, id: params.row.id, name: params.row.name })}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1400 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Downloads</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage downloadable artifacts — agents, collectors, SDKs, and other binaries available to tenants.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={() => { fetchArtifacts(); fetchCategories(); }} disabled={loading}>
                        Refresh
                    </Button>
                    <Button startIcon={<ManageIcon />} onClick={openCategoryCreate}>
                        Manage Categories
                    </Button>
                    <Button variant="contained" startIcon={<UploadIcon />} onClick={handleOpenUpload}>
                        Upload Artifact
                    </Button>
                </Box>
            </Box>

            {/* Categories row — compact management strip under the header.
                Lets the operator see/edit/delete categories without opening a
                separate page. Create happens via the header button. */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <CategoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        Categories:
                    </Typography>
                    {categories.map((c) => {
                        const color = '#' + (c.color || '6B7280');
                        return (
                            <Chip
                                key={c.slug}
                                size="small"
                                label={c.name}
                                onClick={() => openCategoryEdit(c)}
                                onDelete={c.slug === 'other' ? undefined : () => deleteCategory(c)}
                                sx={{
                                    bgcolor: alpha(color, 0.12),
                                    color: color,
                                    fontWeight: 600,
                                    border: `1px solid ${alpha(color, 0.3)}`,
                                    cursor: 'pointer',
                                }}
                            />
                        );
                    })}
                    <Button size="small" startIcon={<AddIcon />} onClick={openCategoryCreate} sx={{ ml: 'auto' }}>
                        Add Category
                    </Button>
                </Stack>
            </Paper>

            {/* Bulk actions toolbar — only visible when rows are selected. */}
            {selection.length > 0 && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: 1, borderColor: 'primary.main',
                       bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" fontWeight={600}>
                            {selection.length} selected
                        </Typography>
                        <Button size="small" startIcon={<EnabledIcon />} onClick={() => handleBulkToggle(true)}>
                            Enable
                        </Button>
                        <Button size="small" startIcon={<DisabledIcon />} onClick={() => handleBulkToggle(false)}>
                            Disable
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setBulkDeleteOpen(true)}>
                            Delete
                        </Button>
                        <Button size="small" sx={{ ml: 'auto' }} onClick={() => setSelection([])}>
                            Clear
                        </Button>
                    </Stack>
                </Paper>
            )}

            {/* Stats Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {statsCards.map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.label}>
                        <Card
                            variant="outlined"
                            sx={{
                                bgcolor: isDark ? alpha(stat.color, 0.06) : alpha(stat.color, 0.04),
                                borderColor: alpha(stat.color, 0.2),
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(stat.color, 0.12),
                                        color: stat.color,
                                    }}
                                >
                                    {stat.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* DataGrid */}
            <Paper sx={{ bgcolor: isDark ? '#0d1117' : '#fff' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <TableSkeleton rows={5} columns={4} />
                    </Box>
                ) : artifacts.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <InventoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary" sx={{ mb: 0.5 }}>No artifacts uploaded</Typography>
                        <Typography variant="body2" color="text.disabled">
                            Upload agents, collectors, or SDKs to make them available for download by tenants.
                        </Typography>
                    </Box>
                ) : (
                    <DataGrid
                        rows={artifacts}
                        columns={columns}
                        getRowId={(row) => row.id}
                        autoHeight
                        disableRowSelectionOnClick
                        checkboxSelection
                        rowSelectionModel={selection}
                        onRowSelectionModelChange={setSelection}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                            },
                        }}
                    />
                )}
            </Paper>

            {/* Upload Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Artifact</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            required
                            value={formData.name}
                            onChange={handleFormChange('name')}
                            placeholder="e.g. Java APM Agent"
                        />
                        <TextField
                            fullWidth
                            label="Slug"
                            required
                            value={formData.slug}
                            onChange={handleFormChange('slug')}
                            placeholder="e.g. java-apm-agent"
                            helperText="URL-safe identifier used for download links"
                            InputProps={{
                                sx: { fontFamily: 'monospace' },
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                label="Category"
                                onChange={handleFormChange('category')}
                            >
                                {categories.map((c) => (
                                    <MenuItem key={c.slug} value={c.slug}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={handleFormChange('description')}
                            placeholder="Brief description of this artifact"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            label="Version"
                            value={formData.version}
                            onChange={handleFormChange('version')}
                            placeholder="e.g. v2.1.0"
                        />
                        <TextField
                            fullWidth
                            label="Requirements"
                            value={formData.requirements}
                            onChange={handleFormChange('requirements')}
                            placeholder="e.g. Java 8+, Linux/macOS/Windows"
                        />
                        <TextField
                            fullWidth
                            label="Install Instructions"
                            value={formData.installInstructions}
                            onChange={handleFormChange('installInstructions')}
                            placeholder="Shown on the tenant download drawer. Supports line breaks."
                            helperText="Plain text / monospace commands. Tenants see this when they open the artifact."
                            multiline
                            rows={4}
                            InputProps={{ sx: { fontFamily: 'monospace' } }}
                        />
                        <TextField
                            fullWidth
                            label="Release Notes"
                            value={formData.releaseNotes}
                            onChange={handleFormChange('releaseNotes')}
                            placeholder="What changed in this version. Shown on the tenant download drawer."
                            helperText="Optional changelog for this version. Tenants see this when they open the artifact."
                            multiline
                            rows={3}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Platform</InputLabel>
                            <Select
                                value={formData.platform}
                                label="Platform"
                                onChange={handleFormChange('platform')}
                            >
                                {PLATFORMS.map((p) => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadIcon />}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                {selectedFile ? selectedFile.name : 'Select File'}
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleFileSelect}
                                />
                            </Button>
                            {selectedFile && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {formatFileSize(selectedFile.size)}
                                </Typography>
                            )}
                        </Box>
                        {uploading && (
                            <Box>
                                <LinearProgress
                                    variant={processing ? 'indeterminate' : 'determinate'}
                                    value={uploadProgress}
                                    sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
                                />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" color="text.secondary">
                                        {processing
                                            ? 'Processing on server…'
                                            : `Uploading… ${uploadProgress}%`}
                                    </Typography>
                                    {selectedFile && (
                                        <Typography variant="caption" color="text.secondary">
                                            {formatFileSize(uploadedBytes)} / {formatFileSize(selectedFile.size)}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={uploading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!formData.name.trim() || !formData.slug.trim() || !selectedFile || uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
                onConfirm={handleDelete}
                title="Delete Artifact"
                message={`Permanently delete "${deleteDialog.name}"? This will remove the file and it will no longer be available for download.`}
                confirmText="Delete"
                confirmColor="error"
                severity="error"
            />

            {/* Bulk Delete Confirmation */}
            <ConfirmDialog
                open={bulkDeleteOpen}
                onClose={() => setBulkDeleteOpen(false)}
                onConfirm={handleBulkDelete}
                title={`Delete ${selection.length} artifact(s)?`}
                message="Selected files will be removed from disk and the catalog. This cannot be undone."
                confirmText="Delete"
                confirmColor="error"
                severity="error"
            />

            {/* Category CRUD dialog */}
            <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {editingCategory ? `Edit "${editingCategory.name}"` : 'New Category'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Slug"
                            fullWidth
                            required
                            value={categoryForm.slug}
                            disabled={!!editingCategory}
                            onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                            helperText={editingCategory ? 'Slug is immutable after create — artifacts reference it.' : 'Lowercase, dashes only. e.g. "sdk-beta".'}
                            InputProps={{ sx: { fontFamily: 'monospace' } }}
                        />
                        <TextField
                            label="Name"
                            fullWidth
                            required
                            value={categoryForm.name}
                            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                            helperText="Display label shown to tenants."
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            value={categoryForm.description}
                            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Color"
                                fullWidth
                                value={categoryForm.color}
                                onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6) })}
                                helperText="Hex, no #"
                                placeholder="8B5CF6"
                                InputProps={{
                                    sx: { fontFamily: 'monospace' },
                                    startAdornment: (
                                        <Box sx={{ width: 16, height: 16, mr: 1, borderRadius: 0.5, border: 1, borderColor: 'divider',
                                                   bgcolor: categoryForm.color ? `#${categoryForm.color}` : 'transparent' }} />
                                    ),
                                }}
                            />
                            <TextField
                                label="Sort Order"
                                type="number"
                                sx={{ width: 140 }}
                                value={categoryForm.sortOrder}
                                onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: Number(e.target.value) || 100 })}
                                helperText="Lower first"
                            />
                        </Stack>
                        <TextField
                            label="Icon (MUI name)"
                            fullWidth
                            value={categoryForm.icon}
                            onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                            placeholder="Memory, Cable, Code, Extension…"
                            helperText="Optional. Frontend resolves to @mui/icons-material."
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={categoryForm.enabled}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, enabled: e.target.checked })}
                                />
                            }
                            label="Enabled (shown to tenants)"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveCategory}
                            disabled={!categoryForm.slug.trim() || !categoryForm.name.trim()}>
                        {editingCategory ? 'Save' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OperatorDownloadsPage;
